import { assert, runInDebug } from '@ember/debug';
import { getOwner } from '@ember/application';
import Service from '@ember/service';

/**
 * SPDS Menu Service
 *
 * Owns the `document` listeners that dismiss open SPDS menus, replacing the per-instance listener
 * the `close-on-click-outside` modifier used to attach. One set serves every registered menu, and
 * `event.composedPath()` is computed once per event instead of once per instance.
 *
 * Two listeners rather than one: a `click` decides dismissal, and a `mousedown` records where the
 * gesture behind it began. The browser reports a `click` on the nearest common ancestor of its
 * press and release, so a drag that starts inside a menu and ends outside arrives with a target
 * above the menu and reads as an outside click. "Inside" therefore means the click landed inside
 * the boundary **or** the gesture that produced it started there.
 *
 * Registration is lifecycle-driven, not open-driven: `Spds::Core::KeyboardNavigable` registers
 * itself from `setup()` and unregisters on teardown, and the click handler reads `open` off each
 * registered instance at click time. Nothing has to tell the service when a menu opens, so no
 * per-template modifier is needed, and close handlers no longer run for menus that are closed.
 *
 * A *dismissable* is anything exposing:
 *
 * - `open` - whether it should participate in dismissal right now
 * - `baseElement` - the containment boundary; a click inside it, or one whose gesture started
 *   inside it, is not "outside"
 * - `dismissSelector` - optional class name that dismisses even when clicked inside the boundary
 *   (used by `Spds::Input::Search` and `Spds::Input::Dropdown::List` for their labels)
 * - `handleClose()` - called when a click lands outside the boundary
 *
 * SPDS menu components satisfy that contract natively. The `close-on-click-outside` modifier
 * supplies an object literal for the app-side call sites that are not menu components.
 *
 * Note: this service is `spds/menu`, which reads like the `Spds::Menu::*` components. Ember keeps
 * the service and component registries separate, so there is no resolver collision.
 */
export default class SpdsMenuService extends Service {
  /**
   * Registered dismissables. Deliberately untracked: it is written from component lifecycle hooks
   * and never read during render, so tracking it would buy nothing and risk a backtracking
   * assertion. Open-ness is resolved from the instances at click time, not from this set.
   * @private
   */
  #dismissables = new Set();

  /** @private */
  #listening = false;

  /**
   * The composed path and target of the press behind the click now in flight, captured while the
   * nodes it ran through are still mounted. The path is what survives a control unrendering
   * mid-gesture, the same reason the click handler prefers it over `contains`.
   * @private
   */
  #pressPath = [];

  /** @private */
  #pressTarget = null;

  /**
   * Resolved once here rather than injected or looked up on demand, because both alternatives throw.
   * `@service fastboot` is validated on instantiation and blows up in any build without
   * `ember-cli-fastboot` registered, while looking it up lazily blows up the other way: the
   * `unregisterMenu` destructor can run after the owner is destroyed, and `lookup` is illegal by
   * then. The constructor always runs while the owner is alive, so resolving here is safe.
   * @private
   */
  #isFastBoot = false;

  constructor() {
    super(...arguments);

    this.#isFastBoot = getOwner(this)?.lookup('service:fastboot')?.isFastBoot ?? false;
  }

  /**
   * the registered dismissables that a click should currently be able to close
   *
   * The destroy check matters because the registry holds instances: a component torn down without
   * its destructor running (or mid-teardown) must not be dispatched to.
   *
   * @return {Array<object>}
   */
  get openMenus() {
    return [...this.#dismissables].filter(
      menu => menu.open && !menu.isDestroying && !menu.isDestroyed
    );
  }

  /**
   * Registers a dismissable. Called once per instance from `Spds::Core::KeyboardNavigable.setup`
   * and from the `close-on-click-outside` modifier.
   *
   * @param {object} dismissable - exposes `open`, `baseElement`, `dismissSelector`, `handleClose`
   */
  registerMenu = dismissable => {
    runInDebug(() => {
      assert(
        'spds/menu: registerMenu expects a dismissable exposing a `handleClose` function',
        typeof dismissable?.handleClose === 'function'
      );
    });

    this.#dismissables.add(dismissable);
    this.#syncListener();
  };

  /**
   * Removes a dismissable from the registry, detaching the listener once nothing is left.
   *
   * @param {object} dismissable - the instance or adapter passed to `registerMenu`
   */
  unregisterMenu = dismissable => {
    this.#dismissables.delete(dismissable);
    this.#syncListener();
  };

  willDestroy() {
    super.willDestroy(...arguments);

    this.#dismissables.clear();
    this.#syncListener();
  }

  /**
   * Attaches the listeners while anything is registered and detaches them once the registry
   * empties, so a torn-down test app leaves none on `document`. No-ops when the desired state
   * already matches, and under FastBoot where there is no `document` to listen on.
   * @private
   */
  #syncListener = () => {
    if (this.#isFastBoot) return;

    let shouldListen = this.#dismissables.size > 0;

    if (shouldListen === this.#listening) return;

    let apply = shouldListen
      ? document.addEventListener.bind(document)
      : document.removeEventListener.bind(document);

    apply('mousedown', this.#onDocumentMouseDown);
    apply('click', this.#onDocumentClick);
    // Capture, so a menu that stops its own Escape from propagating cannot keep the press alive.
    apply('keydown', this.#forgetPress, true);

    this.#listening = shouldListen;
  };

  /**
   * Resolves whether an event happened inside `element`, preferring the composed path captured at
   * dispatch time so a node removed from the DOM during the same gesture still counts as inside.
   * Falls back to `contains` for older event targets.
   * @private
   */
  #containsEvent(element, path, target) {
    return path.includes(element) || (!!target && element.contains(target));
  }

  /**
   * Records the gesture behind the click that is about to arrive. Keeps the same fast path as the
   * click handler: with nothing open there is nothing a press could later exempt.
   *
   * There is deliberately no `mouseup` counterpart. `Spds::Core::Modal` needs one so a gesture that
   * begins outside and ends inside stops counting, but a menu dismisses on the press location
   * alone - and `mouseup` runs before `click`, so anything cleared there would be gone before the
   * click that needs it.
   * @private
   */
  #onDocumentMouseDown = event => {
    if (!this.openMenus.length) return this.#forgetPress();

    this.#pressPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    this.#pressTarget = event.target;
  };

  /**
   * Also runs on every `keydown`: a press that never became a click (Escape mid-gesture, a
   * right-click, a drag released outside the window) must not exempt a keyboard-activated click.
   * @private
   */
  #forgetPress = () => {
    this.#pressPath = [];
    this.#pressTarget = null;
  };

  /**
   * @private
   */
  #onDocumentClick = event => {
    // Bail before touching `composedPath()`, which allocates an array of the full DOM path. With
    // nothing open this is a handful of boolean reads per click. The press still has to go: one
    // captured while a menu was open must not outlive it and exempt a later press-less click.
    let openMenus = this.openMenus;

    if (!openMenus.length) return this.#forgetPress();

    let { target } = event;
    let path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    // A listener per instance used to give each menu error isolation for free, since the browser
    // catches per listener. With one shared listener a throwing consumer `onClose` would otherwise
    // stop every menu after it from dismissing, so the first failure is held and rethrown at the
    // end - out of the listener, where the browser reports it exactly as it did before.
    let failure;

    // `openMenus` is a fresh array, so it is the snapshot: a handler that unregisters its own menu
    // (or another's) mid-iteration does not disturb the loop.
    for (let menu of openMenus) {
      let element = menu.baseElement;

      if (!element) continue;

      // Either the click landed inside, or the gesture behind it started there. The second is
      // additive on purpose: a click with no recorded press - keyboard activation, a programmatic
      // dispatch - is judged exactly as it was before any of this existed.
      let clickedInside =
        this.#containsEvent(element, path, target) ||
        this.#containsEvent(element, this.#pressPath, this.#pressTarget);

      if (clickedInside && !target.classList?.contains(menu.dismissSelector)) continue;

      try {
        menu.handleClose();
      } catch (error) {
        failure ??= error;
      }
    }

    // The gesture is spent. Leaving it would let a press inside a menu exempt some later click
    // that had no press of its own.
    this.#forgetPress();

    if (failure) throw failure;
  };
}
